import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BRAND } from "../config/brand";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-xl lg:text-2xl font-semibold text-white tracking-tight mb-4">
      {title}
    </h2>
    <div className="font-body text-sm lg:text-base text-zinc-300 leading-relaxed space-y-2">
      {children}
    </div>
  </section>
);

const Impressum = () => {
  useEffect(() => {
    document.title = "Impressum · LionPerformance";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="page-impressum"
      className="min-h-screen bg-black text-white"
    >
      <header className="border-b border-white/5">
        <div className="mx-auto max-w-[900px] px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="impressum-home"
            className="flex items-center gap-3 group"
          >
            <img
              src={BRAND.logo}
              alt="LionPerformance"
              className="h-9 w-9 object-contain"
              style={{ filter: "url(#lp-remove-black)" }}
            />
            <span className="font-display text-white text-base font-semibold tracking-tight">
              Lion<span className="text-primary-red">Performance</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="impressum-back"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-primary-red transition-colors"
          >
            <ArrowLeft size={14} />
            Zurück
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-[900px] px-6 lg:px-8 py-14 lg:py-20">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-primary-red" />
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-400">
            Rechtliches
          </span>
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.05] mb-4">
          Impressum
        </h1>
        <p className="text-sm text-zinc-500 mb-14">Angaben gemäß § 5 DDG</p>

        <Section title="Anbieter">
          <p className="font-semibold text-white">Lion Performance</p>
          <p>Inhaber: Marc Wiebeck</p>
          <p>Rechtsform: Einzelunternehmen</p>
          <p className="pt-2">Helenenstraße 19b</p>
          <p>38118 Braunschweig</p>
          <p>Deutschland</p>
        </Section>

        <Section title="Kontakt">
          <p>
            Telefon:{" "}
            <a
              href="tel:+4915110662067"
              className="text-white hover:text-primary-red transition-colors"
            >
              +49 (0)151 10662067
            </a>
          </p>
          <p>
            E-Mail:{" "}
            <a
              href="mailto:info@lionperformance.fit"
              className="text-white hover:text-primary-red transition-colors"
            >
              info@lionperformance.fit
            </a>
          </p>
        </Section>

        <Section title="Umsatzsteuer">
          <p>
            Derzeit liegt keine Umsatzsteuer-Identifikationsnummer gemäß § 27a
            Umsatzsteuergesetz vor.
          </p>
        </Section>

        <Section title="Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV">
          <p>Marc Wiebeck</p>
          <p>Helenenstraße 19b</p>
          <p>38118 Braunschweig</p>
          <p>Deutschland</p>
        </Section>

        <Section title="Tätigkeitsbereich">
          <p>
            Lion Performance bietet Coaching-, Beratungs- und
            Bildungsleistungen im Bereich Sportwissenschaft,
            Sportpsychologie, mentale Leistungsoptimierung und High
            Performance Coaching an.
          </p>
          <p className="pt-3 font-semibold text-white">Berufsbezeichnung:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Sportwissenschaftler mit Spezialisierung Sportpsychologie
              (M.Sc.)
            </li>
            <li>High Performance Coach</li>
            <li>Mental Coach</li>
          </ul>
        </Section>

        <Section title="Räumlicher Geltungsbereich">
          <p>
            Die angebotenen Leistungen richten sich grundsätzlich an Kunden
            innerhalb Deutschlands. Sofern gesetzlich zulässig, können
            Leistungen auch international erbracht werden.
          </p>
        </Section>

        <Section title="Online-Streitbeilegung gemäß Art. 14 ODR-Verordnung">
          <p>
            Die Europäische Kommission stellt eine Plattform zur
            Online-Streitbeilegung (OS) bereit:
          </p>
          <p>
            <a
              href="https://ec.europa.eu/consumers/odr/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-primary-red transition-colors break-all"
            >
              https://ec.europa.eu/consumers/odr/
            </a>
          </p>
          <p className="pt-2">
            Unsere E-Mail-Adresse lautet:{" "}
            <a
              href="mailto:info@lionperformance.fit"
              className="text-white hover:text-primary-red transition-colors"
            >
              info@lionperformance.fit
            </a>
          </p>
        </Section>

        <Section title="Verbraucherstreitbeilegung (§ 36 VSBG)">
          <p>
            Wir sind weder verpflichtet noch bereit, an
            Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
            teilzunehmen.
          </p>
        </Section>

        <Section title="Social Media">
          <p>
            Instagram:{" "}
            <a
              href="https://www.instagram.com/lionperformance/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-primary-red transition-colors"
            >
              @lionperformance
            </a>
          </p>
        </Section>

        <Section title="Haftung für Inhalte">
          <p>
            Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene
            Inhalte auf diesen Seiten nach den allgemeinen Gesetzen
            verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter
            jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde
            Informationen zu überwachen oder nach Umständen zu forschen, die
            auf eine rechtswidrige Tätigkeit hinweisen.
          </p>
          <p>
            Verpflichtungen zur Entfernung oder Sperrung der Nutzung von
            Informationen nach den allgemeinen Gesetzen bleiben hiervon
            unberührt. Eine entsprechende Haftung ist jedoch erst ab dem
            Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich.
            Bei Bekanntwerden entsprechender Rechtsverletzungen werden wir
            diese Inhalte unverzüglich entfernen.
          </p>
        </Section>

        <Section title="Haftung für Links">
          <p>
            Unser Angebot enthält gegebenenfalls Links zu externen Websites
            Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb
            können wir für diese fremden Inhalte keine Gewähr übernehmen. Für
            die Inhalte der verlinkten Seiten ist stets der jeweilige
            Anbieter oder Betreiber der Seiten verantwortlich.
          </p>
        </Section>

        <Section title="Urheberrecht">
          <p>
            Die durch den Seitenbetreiber erstellten Inhalte und Werke auf
            dieser Website unterliegen dem deutschen Urheberrecht. Jede
            Vervielfältigung, Bearbeitung, Verbreitung oder sonstige
            Verwertung außerhalb der Grenzen des Urheberrechts bedarf der
            vorherigen schriftlichen Zustimmung des jeweiligen Rechteinhabers.
          </p>
        </Section>

        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-zinc-600">
            © {new Date().getFullYear()} Lion Performance. Alle Rechte
            vorbehalten.
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 hover:text-primary-red transition-colors"
          >
            <ArrowLeft size={14} />
            Zurück zur Startseite
          </Link>
        </div>
      </article>
    </main>
  );
};

export default Impressum;
